import useYupValidationResolver from 'form/yup/reactHookFormResolver';
import React, { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useHistory } from 'react-router';
import { registerPersonalDetails, registerRegisterFirm, registerSearchFirms } from 'routes/routePaths';
import Navigation from '../Form/Navigation/Navigation';
import { Heading, Paragraph, Section } from '../UI';
import { FIELD_LABELS, FIELD_NAMES, Form, POLICY_ANSWERS } from './types';
import { validationSchema } from './validationSchema';

import { Checkbox, NumericInput, RadioGroup, TextArea, TextInput } from 'form/infrastructure/reactHookForm';
import FormInputWrapper from 'form/infrastructure/reactHookForm/FormInputWrapper';
import { Box, Column, Grid } from 'lmc-ui-library';
import canViewDueDiligenceStep from 'screens/BrokerPortal/screens/Registration/guard/canViewDueDiligenceStep';
import { Step } from 'screens/BrokerPortal/screens/Registration/steps';
import { State } from 'screens/BrokerPortal/screens/Registration/store/types';
import styled from 'styled-components';
import { getLatestTermsOfBusinessData } from '../../api';

const SectionWrapper = styled.div`
  label {
    display: block;
    max-width: 428px;
  }

  div {
    gap: 40px;
  }
`;

interface Props {
  form: Form;
  searchFirmForm: State['searchFirms']['form'];
  setCurrentStep: (step: Step) => void;
  setDueDiligenceForm: (data: Form) => void;
}

const DueDiligenceStep: React.FC<Props> = ({ form, setDueDiligenceForm, setCurrentStep, searchFirmForm }) => {
  const methods = useForm<Form>({
    mode: 'onBlur',
    resolver: useYupValidationResolver(validationSchema()),
    defaultValues: form,
  });
  const {
    handleSubmit,
    trigger,
    getValues,
    watch,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    formState: { isValid },
  } = methods;
  const history = useHistory();
  const [termsOfBusinessUrl, setTermsOfBusinessUrl] = useState<string>('');
  const watchNumberOfComplaintsReceived = watch(FIELD_NAMES.NUMBER_OF_COMPLAINTS_RECEIVED);

  const onSubmit = handleSubmit((data) => setDueDiligenceForm(data));

  const handleBack = () => history.push(registerRegisterFirm);

  useEffect(() => {
    if (!canViewDueDiligenceStep(searchFirmForm)) {
      history.push(registerSearchFirms);
    }
  }, [history, searchFirmForm]);

  useEffect(() => {
    setCurrentStep(Step.DUE_DILIGENCE);
  }, [setCurrentStep]);

  useEffect(() => {
    const notEmptyFields = (Object.keys(getValues()) as [keyof Form]).filter(
      (key) => getValues()[key] !== null && getValues()[key] !== '',
    );
    if (notEmptyFields.length > 0) {
      trigger(notEmptyFields);
    }
  }, [getValues, trigger]);

  useEffect(() => {
    getLatestTermsOfBusinessData().then((data) => setTermsOfBusinessUrl(data.url));
  }, []);

  const handleContinue = async () => {
    if (!(await trigger())) {
      return;
    }

    await onSubmit();
    history.push(registerPersonalDetails);
  };

  return (
    <Box>
      <Box.Content>
        <FormProvider<Form> {...methods}>
          <Grid cols={12}>
            <Column lg={7} md={9} sm={12} xs={12}>
              <Heading variant="h1">Firm due diligence</Heading>
              <Paragraph>Please complete the following due dilligence questions on behalf of your firm.</Paragraph>
            </Column>
          </Grid>
          <form onSubmit={onSubmit}>
            <Grid cols={12}>
              <Column lg={7} md={9} sm={12} xs={12}>
                <Section>
                  <FormInputWrapper
                    title={FIELD_LABELS.COMPANIES_HOUSE_NUMBER}
                    name={FIELD_NAMES.COMPANIES_HOUSE_NUMBER}
                    extraContent={[
                      {
                        position: 'bottom',
                        variant: 'text',
                        content: (
                          <span>
                            If you don’t know your number, please search{' '}
                            <a
                              href="https://find-and-update.company-information.service.gov.uk/search/companies"
                              target="_blank"
                              rel="noopener noreferrer">
                              here
                            </a>
                          </span>
                        ),
                      },
                    ]}>
                    <TextInput />
                  </FormInputWrapper>
                  <FormInputWrapper title={FIELD_LABELS.MANAGEMENT_DOB} name={FIELD_NAMES.MANAGEMENT_DOB}>
                    <TextArea rows={8} />
                  </FormInputWrapper>
                  <FormInputWrapper
                    title={FIELD_LABELS.ICO_PROTECTION_NUMBER}
                    name={FIELD_NAMES.ICO_PROTECTION_NUMBER}
                    extraContent={[
                      {
                        position: 'bottom',
                        variant: 'text',
                        content: (
                          <span>
                            If you don’t know your number, please search{' '}
                            <a href="https://ico.org.uk/ESDWebPages/Search" target="_blank" rel="noopener noreferrer">
                              here
                            </a>
                          </span>
                        ),
                      },
                    ]}>
                    <TextInput />
                  </FormInputWrapper>

                  <FormInputWrapper
                    title={FIELD_LABELS.NUMBER_OF_COMPLAINTS_RECEIVED}
                    name={FIELD_NAMES.NUMBER_OF_COMPLAINTS_RECEIVED}>
                    <NumericInput wrapperStyle={{ width: '100px' }} />
                  </FormInputWrapper>

                  {watchNumberOfComplaintsReceived !== 0 && watchNumberOfComplaintsReceived !== null && (
                    <FormInputWrapper
                      title={FIELD_LABELS.NUMBER_OF_COMPLAINTS_UPHELD}
                      name={FIELD_NAMES.NUMBER_OF_COMPLAINTS_UPHELD}>
                      <NumericInput wrapperStyle={{ width: '100px' }} />
                    </FormInputWrapper>
                  )}

                  <FormInputWrapper
                    title={FIELD_LABELS.WHO_CARRIES_COMPLIANCE_OVERSIGHT}
                    name={FIELD_NAMES.WHO_CARRIES_COMPLIANCE_OVERSIGHT}>
                    <TextArea rows={8} />
                  </FormInputWrapper>
                </Section>
              </Column>
            </Grid>

            <Section>
              <SectionWrapper>
                <Heading variant="h2">
                  Do you have the following policies.
                  <br />
                  If no, please explain.
                </Heading>

                <FormInputWrapper title={FIELD_LABELS.FINANCIAL_CRIME_POLICY} name={FIELD_NAMES.FINANCIAL_CRIME_POLICY}>
                  <RadioGroup
                    name={FIELD_NAMES.FINANCIAL_CRIME_POLICY}
                    isDisplayFlex={true}
                    options={[
                      {
                        value: POLICY_ANSWERS.YES,
                        text: <>Yes</>,
                      },
                      {
                        value: POLICY_ANSWERS.NO,
                        text: <>No (please explain below)</>,
                      },
                      {
                        value: POLICY_ANSWERS.DONT_KNOW,
                        text: <>Don't know (please explain below)</>,
                      },
                    ]}
                  />
                </FormInputWrapper>

                <FormInputWrapper title={FIELD_LABELS.DATA_PROTECTION_POLICY} name={FIELD_NAMES.DATA_PROTECTION_POLICY}>
                  <RadioGroup
                    name={FIELD_NAMES.FINANCIAL_CRIME_POLICY}
                    isDisplayFlex={true}
                    options={[
                      {
                        value: POLICY_ANSWERS.YES,
                        text: <>Yes</>,
                      },
                      {
                        value: POLICY_ANSWERS.NO,
                        text: <>No (please explain below)</>,
                      },
                      {
                        value: POLICY_ANSWERS.DONT_KNOW,
                        text: <>Don't know (please explain below)</>,
                      },
                    ]}
                  />
                </FormInputWrapper>

                <FormInputWrapper
                  title={FIELD_LABELS.TREATING_CUSTOMER_FAIRLY_POLICY}
                  name={FIELD_NAMES.TREATING_CUSTOMER_FAIRLY_POLICY}>
                  <RadioGroup
                    name={FIELD_NAMES.FINANCIAL_CRIME_POLICY}
                    isDisplayFlex={true}
                    options={[
                      {
                        value: POLICY_ANSWERS.YES,
                        text: <>Yes</>,
                      },
                      {
                        value: POLICY_ANSWERS.NO,
                        text: <>No (please explain below)</>,
                      },
                      {
                        value: POLICY_ANSWERS.DONT_KNOW,
                        text: <>Don't know (please explain below)</>,
                      },
                    ]}
                  />
                </FormInputWrapper>

                <FormInputWrapper
                  title={FIELD_LABELS.DISASTER_RECOVERY_POLICY}
                  name={FIELD_NAMES.DISASTER_RECOVERY_POLICY}>
                  <RadioGroup
                    name={FIELD_NAMES.FINANCIAL_CRIME_POLICY}
                    isDisplayFlex={true}
                    options={[
                      {
                        value: POLICY_ANSWERS.YES,
                        text: <>Yes</>,
                      },
                      {
                        value: POLICY_ANSWERS.NO,
                        text: <>No (please explain below)</>,
                      },
                      {
                        value: POLICY_ANSWERS.DONT_KNOW,
                        text: <>Don't know (please explain below)</>,
                      },
                    ]}
                  />
                </FormInputWrapper>

                <FormInputWrapper
                  title={FIELD_LABELS.EMPLOYEE_ACCESS_REMOVAL_PROCESS}
                  name={FIELD_NAMES.EMPLOYEE_ACCESS_REMOVAL_PROCESS}>
                  <RadioGroup
                    name={FIELD_NAMES.EMPLOYEE_ACCESS_REMOVAL_PROCESS}
                    isDisplayFlex={true}
                    options={[
                      {
                        value: POLICY_ANSWERS.YES,
                        text: <>Yes</>,
                      },
                      {
                        value: POLICY_ANSWERS.NO,
                        text: <>No (please explain below)</>,
                      },
                      {
                        value: POLICY_ANSWERS.DONT_KNOW,
                        text: <>Don't know (please explain below)</>,
                      },
                    ]}
                  />
                </FormInputWrapper>

                <FormInputWrapper
                  title={FIELD_LABELS.PII_IN_PLACE_WITH_FCA_REQUIREMENTS}
                  name={FIELD_NAMES.PII_IN_PLACE_WITH_FCA_REQUIREMENTS}>
                  <RadioGroup
                    name={FIELD_NAMES.PII_IN_PLACE_WITH_FCA_REQUIREMENTS}
                    isDisplayFlex={true}
                    options={[
                      {
                        value: POLICY_ANSWERS.YES,
                        text: <>Yes</>,
                      },
                      {
                        value: POLICY_ANSWERS.NO,
                        text: <>No (please explain below)</>,
                      },
                      {
                        value: POLICY_ANSWERS.DONT_KNOW,
                        text: <>Don't know (please explain below)</>,
                      },
                    ]}
                  />
                </FormInputWrapper>
                <FormInputWrapper title={FIELD_LABELS.SENSITIVE_DATA_POLICY} name={FIELD_NAMES.SENSITIVE_DATA_POLICY}>
                  <RadioGroup
                    name={FIELD_NAMES.SENSITIVE_DATA_POLICY}
                    isDisplayFlex={true}
                    options={[
                      {
                        value: POLICY_ANSWERS.YES,
                        text: <>Yes</>,
                      },
                      {
                        value: POLICY_ANSWERS.NO,
                        text: <>No (please explain below)</>,
                      },
                      {
                        value: POLICY_ANSWERS.DONT_KNOW,
                        text: <>Don't know (please explain below)</>,
                      },
                    ]}
                  />
                </FormInputWrapper>
              </SectionWrapper>
            </Section>

            <Grid cols={12}>
              <Column lg={7} md={9} sm={12} xs={12}>
                <Section>
                  <Heading variant="h2">Additional information</Heading>

                  <FormInputWrapper
                    title={FIELD_LABELS.ADDITIONAL_INFORMATION}
                    name={FIELD_NAMES.ADDITIONAL_INFORMATION}>
                    <TextArea rows={8} />
                  </FormInputWrapper>
                </Section>

                <Section>
                  <FormInputWrapper name={FIELD_NAMES.AGREED_TO_TERMS_AND_CONDITIONS} title="">
                    <Checkbox
                      title=""
                      customTitle={
                        <label style={{ lineHeight: '26px' }}>
                          I confirm that I have read and understand, and am authorised by the Intermediary either as, or
                          by, a Director or FCA Senior Management Function holder (SMF) to accept and sign the{' '}
                          <a href={termsOfBusinessUrl} target="_blank" rel="noopener noreferrer">
                            Intermediary Terms of Business
                          </a>
                          .
                        </label>
                      }
                    />
                  </FormInputWrapper>
                </Section>

                <Navigation onBack={handleBack} onContinue={handleContinue} continueDisabled={false} />
              </Column>
            </Grid>
          </form>
        </FormProvider>
      </Box.Content>
    </Box>
  );
};

export default DueDiligenceStep;
